import type { ReactNode } from "react";
import { Navigate, useLocation, useParams } from "react-router";
import { Section } from "../../components/Section";
import { useAuth } from "./context";
import { useCatalogue, useEntitlements, useMe } from "../catalogue/queries";
import { canAccess } from "../catalogue/access";
import { TERMS_VERSION } from "../../content/legal";

/**
 * Route guards.
 *
 * All of these decide what the UI SHOWS. None of them is security — the server
 * re-checks entitlement on every request, because anyone with a token and curl
 * walks straight past this file.
 */

/**
 * Full-page skeleton while boot state resolves. Not a spinner-in-a-corner.
 *
 * `why` NAMES THE GUARD AND THE REASON, in a data attribute. Invisible to users,
 * one glance in the element inspector.
 *
 * It is here because four separate guards render this identical screen, so a
 * report of "it says LOADING…" narrows the cause to one of four code paths with
 * different fixes — and a stuck spinner is the one failure that leaves nothing
 * else behind: no console error, no failed request, no URL change. Chasing one
 * on 2026-08-08 cost several wrong diagnoses in a row, each individually
 * plausible, because the screen that was showing said nothing about which of
 * them it was. This is the cheapest possible way for it never to happen again.
 */
function Booting({ why }: { why: string }) {
  return (
    <Section tone="ink">
      <p
        data-booting={why}
        style={{ fontFamily: "var(--font-data)", fontSize: 12, letterSpacing: "0.14em", color: "var(--muted-small)" }}
      >
        LOADING…
      </p>
    </Section>
  );
}

/**
 * Requires a session.
 *
 * The `status === "loading"` branch is the load-bearing one. Without it, the
 * guard sees `session === null` on first paint and bounces to /login before
 * restoreSession() has resolved — every reload flashes the login screen. That
 * matters doubly here: the Stripe round-trip is a real page leave, so a paying
 * user hits this exact path on the way back.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === "loading") return <Booting why="RequireAuth:session" />;
  if (status === "anonymous") {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return <>{children}</>;
}

/**
 * 18+ gate (guardrails §3.3, plan §8C).
 *
 * Wraps `/` and `/app` — both render Home, the fixture list — and the rest
 * of the dashboard, deliberately not the marketing page (`/why-kev`). A
 * blocking modal on marketing copy kills conversion and isn't required for
 * marketing content; the persistent footer carries the 18+ mark everywhere.
 * It no-ops for an anonymous visitor regardless (see the claim check below),
 * which is what lets it sit on a page a stranger and a crawler both reach.
 */
export function RequireAdult({ children }: { children: ReactNode }) {
  const { status, session } = useAuth();
  const me = useMe();

  if (status === "loading") return <Booting why="RequireAdult:session" />;

  /*
   * THE CLAIM IS CHECKED FIRST, AND THE ORDER IS THE WHOLE POINT.
   *
   * A token that says dobVerified passes immediately and never touches /me.
   * That is what this guard has always done, and it must keep doing it: it is
   * the answer for every account that has ever completed the gate, it costs
   * nothing, and it cannot fail.
   *
   * The first version of this change put `me.isLoading` in the line above,
   * which made EVERY visit to /app wait on a network call the guard did not
   * need. A /me that was slow, erroring, or — as happened — pointed at a Lambda
   * that had not been deployed yet, left the whole app on a permanent LOADING…
   * for users whose claim was perfectly good. A fallback must never be able to
   * block the primary path.
   */
  if (!session || session.dobVerified) return <>{children}</>;

  /*
   * Only now does /me matter, and it is asked the ONE question the server will
   * enforce: profile.dobVerified, computed by kev_security.is_adult — the same
   * function require_adult() gates on.
   *
   * This used to infer from `consent.acceptedTerms`, which is a different fact,
   * and the two drifted immediately: the SPA admitted anyone with a consent row
   * while the API demanded a verified date of birth. The punter reached chat and
   * every turn came back `age_unverified` in a red box. A client that decides
   * eligibility on its own terms will always eventually disagree with the server
   * that enforces it — so it does not decide, it reads.
   *
   * Waiting here is safe in a way that waiting above the claim check was not: we
   * only reach this line for accounts the claim has ALREADY refused, so the
   * worst case is a spinner for someone heading to /welcome anyway.
   *
   * This does NOT weaken the control. require_adult() runs on every billable
   * request. Anything here decides what the UI shows, and this file's header has
   * always said so.
   */
  if (me.isLoading) return <Booting why="RequireAdult:me" />;
  if (me.data?.profile.dobVerified === true) return <>{children}</>;

  /* /welcome, not /signup/age — that route was retired on 2026-08-07. The date
     of birth is now collected in the same act as the consent, so there is one
     page to send an incomplete account to rather than two that could each
     bounce to the other. */
  return <Navigate to="/welcome" replace />;
}

/**
 * Onboarding gate — has this account ever agreed to anything?
 *
 * It exists because an account can be created WITHOUT passing through our page
 * at all. Cognito's sign-in page carries a "New user? Create an account" link
 * to its own sign-up form, and that URL can be bookmarked. Managed Login's
 * branding is visual-only, so the link cannot be removed and no front-door fix
 * closes it. The only thing that can is a check the app makes about the
 * account, which is this.
 *
 * Modelled on RequireEntitlement rather than RequireAdult, because it reads a
 * React Query result and therefore needs the same `isLoading`-not-`isPending`
 * handling documented there — a disabled v5 query sits in `pending` forever and
 * would wedge this on a permanent <Booting />.
 */
export function RequireOnboarded({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const me = useMe();

  if (status === "loading") return <Booting why="RequireOnboarded:session" />;
  if (me.isLoading) return <Booting why="RequireOnboarded:me" />;

  /*
   * FAIL OPEN on a fetch error — the one place this departs from
   * RequireEntitlement, and deliberately.
   *
   * Failing closed here means redirecting to /welcome, whose own submit is a
   * call to the same API. If /me is erroring, that page cannot let them out,
   * and the user is stuck in a redirect they have no way to satisfy. An
   * ungated page for the duration of an outage is the smaller failure —
   * particularly since the security-relevant half of this (the 18+ check) is
   * now enforced server-side on /chat regardless of what this guard decides.
   */
  if (!me.data) return <>{children}</>;

  /* Two different facts, and — since 2026-08-26 — two different destinations.
     Both used to go to /welcome, and for the second one that was harmful rather
     than merely wrong. See TermsUpdated.tsx. */
  const { acceptedTerms, termsVersion } = me.data.consent;

  /* NEVER AGREED TO ANYTHING. They came in through Cognito's own sign-up form,
     so nothing about them has been asked yet — name, age, country, consent. The
     full page is the right one, and its copy ("finish setting up", "the only
     time you'll be asked") is true for them. */
  if (!acceptedTerms) return <Navigate to="/welcome" replace />;

  if (termsVersion !== TERMS_VERSION) {
    /* AGREED ONCE, TO A DOCUMENT WE HAVE SINCE REPLACED. This is what makes
       legal.ts's "reasonable notice before it takes effect" a mechanism rather
       than an intention: bump TERMS_VERSION and everyone is re-asked.

       `dobVerified` is the test for whether they have actually been through
       onboarding, rather than the presence of a name — it is written by the
       SERVER from a date the server checked, so it cannot be true for an
       account that never completed the gate. An account that consented but
       somehow never got verified is an incomplete onboarding, not a
       re-acceptance, and still needs the full page. */
    return me.data.profile.dobVerified ? (
      <Navigate to="/terms-updated" replace />
    ) : (
      <Navigate to="/welcome" replace />
    );
  }

  return <>{children}</>;
}

/**
 * Entitlement gate for /app/m/:modelId.
 *
 * Every branch here is a DEEP-LINK case. The directory tiles already handle the
 * happy path — this exists for pasted and bookmarked URLs, which is exactly when
 * people hit states the UI never offers them.
 */
export function RequireEntitlement({ children }: { children: ReactNode }) {
  const { modelId } = useParams();
  const { session, status } = useAuth();
  const catalogue = useCatalogue();
  const entitlements = useEntitlements();

  /*
   * `isLoading`, not `isPending`.
   *
   * In React Query v5 a query that is disabled (`enabled: false`) sits in
   * `pending` forever — isPending stays true and nothing ever resolves. Gating
   * on isPending therefore wedges this guard on a permanent skeleton the moment
   * useEntitlements is disabled, or while a retry is in flight after a failed
   * fetch. isLoading is `pending && fetching`, which is the question actually
   * being asked here: are we still waiting on the network?
   *
   * If entitlements ERROR, we fall through with `data === undefined`, and
   * canAccess treats that as not-entitled — fail closed, not open.
   */
  if (status === "loading") return <Booting why="RequireEntitlement:session" />;
  if (catalogue.isLoading) return <Booting why="RequireEntitlement:catalogue" />;
  if (entitlements.isLoading) return <Booting why="RequireEntitlement:entitlements" />;

  const model = catalogue.data?.models.find((m) => m.id === modelId);
  /* An unknown id IS a 404 — unlike a real-but-unbuilt model, which isn't. */
  if (!model) return <Navigate to="/app?unknown=1" replace />;

  const decision = canAccess(model, session, entitlements.data ?? null);
  if (decision.allowed) return <>{children}</>;

  switch (decision.reason) {
    case "not-built":
      /* Back to the directory with an inline explanation — NOT a 404. A 404 on a
         real product you're about to sell reads as a broken site. */
      return <Navigate to={`/app?unavailable=${model.id}`} replace />;
    case "signed-out":
      return <Navigate to="/login" replace state={{ from: `/app/m/${model.id}` }} />;
    case "not-entitled":
      /* Deep-link the upsell to the specific model and preserve intent, so a
         purchase returns them to the chat they actually wanted. */
      return (
        <Navigate
          to={`/pricing?model=${model.id}&next=${encodeURIComponent(`/app/m/${model.id}`)}`}
          replace
        />
      );
  }
}
